import { useEffect, useState } from 'react';
import {
  GraduationCap, Play, CheckCircle2, Clock, Flame, Plus, Sparkles, X,
  BarChart3, Users, ArrowRight, Loader2, Trophy,
} from 'lucide-react';
import { toast } from 'sonner';
import { ops, isMgmt } from '../lib/ops';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const STATUS = {
  not_started: { label: 'Not started', cls: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
};

function Ring({ pct, size = 68 }) {
  const r = (size - 8) / 2, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" className="stroke-white/25" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="6" className="stroke-white" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: 'stroke-dashoffset .6s ease' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-white font-black text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>{pct}%</span>
    </div>
  );
}

function MediaBlock({ m }) {
  if (m.video_url) {
    const yt = m.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
    if (yt) return <iframe className="w-full aspect-video rounded-xl border-0" src={`https://www.youtube.com/embed/${yt[1]}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={m.title} data-testid="module-video" />;
    return <video className="w-full aspect-video rounded-xl bg-black" src={m.video_url} controls poster={m.cover_url} data-testid="module-video" />;
  }
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-100" data-testid="module-video-placeholder">
      {m.cover_url && <img src={m.cover_url} className="w-full h-full object-cover" alt={m.title} />}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/35 gap-2">
        <Play className="w-10 h-10 text-white/90" />
        <span className="text-white text-xs font-medium bg-black/50 px-3 py-1.5 rounded-full">Sample module · training video coming soon</span>
      </div>
    </div>
  );
}

function QuizModal({ module, onClose, onDone }) {
  const [view, setView] = useState('intro'); // intro | quiz | result
  const [answers, setAnswers] = useState(() => (module.quiz || []).map(() => -1));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const start = async () => {
    try { await ops.post(`/training/progress/${module.id}/start`); } catch {}
    setView('quiz');
  };
  const submit = async () => {
    if (answers.includes(-1)) { toast.error('Please answer every question'); return; }
    setSubmitting(true);
    try {
      const res = await ops.post(`/training/progress/${module.id}/submit`, { answers });
      setResult(res); setView('result');
      if (res.passed) { toast.success(`Passed with ${res.score}%!`); onDone?.(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="module-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#3E2418] pr-6" style={{ fontFamily: 'Chivo, sans-serif' }}>{module.title}</DialogTitle>
          <DialogDescription className="sr-only">Training module video and knowledge check</DialogDescription>
        </DialogHeader>

        {view === 'intro' && (
          <div className="space-y-4">
            <MediaBlock m={module} />
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2.5 py-1"><Clock className="w-3 h-3" />{module.duration_min || 3} min</span>
              <span className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2.5 py-1">{module.category}</span>
              <span className="inline-flex items-center gap-1 bg-slate-100 rounded-full px-2.5 py-1">{(module.quiz || []).length} questions</span>
            </div>
            {module.description && <p className="text-sm text-slate-600 leading-relaxed">{module.description}</p>}
            {(module.quiz || []).length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">No knowledge check has been added to this module yet.</p>
            ) : (
              <Button onClick={start} className="w-full bg-[#F26522] hover:bg-[#d9551a] text-white rounded-xl h-11 font-semibold" data-testid="start-quiz-btn">
                Watch & take knowledge check <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}

        {view === 'quiz' && (
          <div className="space-y-5">
            {(module.quiz || []).map((q, qi) => (
              <div key={qi} data-testid={`quiz-q-${qi}`}>
                <p className="text-sm font-semibold text-slate-800 mb-2">{qi + 1}. {q.q}</p>
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers(a => a.map((v, i) => i === qi ? oi : v))}
                      data-testid={`quiz-q-${qi}-opt-${oi}`}
                      className={`w-full text-left text-sm rounded-xl border px-3 py-2.5 transition-colors ${answers[qi] === oi ? 'border-[#F26522] bg-orange-50 text-[#8a3c0d] font-medium' : 'border-slate-200 hover:border-slate-300 text-slate-700'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={submit} disabled={submitting} className="w-full bg-[#3E2418] hover:bg-[#2a180f] text-white rounded-xl h-11 font-semibold" data-testid="submit-quiz-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit knowledge check'}
            </Button>
          </div>
        )}

        {view === 'result' && result && (
          <div className="text-center py-4 space-y-3" data-testid="quiz-result">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${result.passed ? 'bg-emerald-100' : 'bg-rose-100'}`}>
              {result.passed ? <CheckCircle2 className="w-10 h-10 text-emerald-600" /> : <X className="w-10 h-10 text-rose-600" />}
            </div>
            <p className="text-3xl font-black text-[#3E2418]" style={{ fontFamily: 'Chivo, sans-serif' }}>{result.score}%</p>
            <p className="text-sm text-slate-600">{result.correct} of {result.total} correct · {result.passed ? 'Module complete!' : 'You need 100% to complete — review the video and try again.'}</p>
            {result.passed
              ? <Button onClick={onClose} className="bg-[#F26522] hover:bg-[#d9551a] text-white rounded-xl px-6" data-testid="result-done-btn">Done</Button>
              : <Button onClick={() => { setAnswers((module.quiz || []).map(() => -1)); setView('quiz'); setResult(null); }} className="bg-[#3E2418] hover:bg-[#2a180f] text-white rounded-xl px-6" data-testid="result-retry-btn">Try again</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const ROLES = ['employee', 'supervisor', 'manager', 'operator'];

function NewModuleModal({ categories, onClose, onCreated }) {
  const [f, setF] = useState({ title: '', category: categories[0] || 'Onboarding & Company', description: '', video_url: '', cover_url: '', duration_min: 3, transcript: '', assigned_roles: [] });
  const [quiz, setQuiz] = useState([]);
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const generate = async () => {
    if (!f.title || !(f.transcript || f.description)) { toast.error('Add a title and a transcript/description first'); return; }
    setGen(true);
    try { const r = await ops.post('/training/generate-quiz-preview', f); setQuiz(r.quiz); toast.success('Quiz generated from the content'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGen(false); }
  };
  const save = async () => {
    if (!f.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try { await ops.post('/training/modules', { ...f, duration_min: Number(f.duration_min) || 3, quiz }); toast.success('Module created'); onCreated(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-module-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>New training module</DialogTitle>
          <DialogDescription className="sr-only">Create a training module with an optional AI-generated quiz</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Allergen Awareness" data-testid="nm-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Category</Label>
              <select value={f.category} onChange={e => set('category', e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="nm-category">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={f.duration_min} onChange={e => set('duration_min', e.target.value)} data-testid="nm-duration" /></div>
          </div>
          <div><Label className="text-xs">Video URL (YouTube or .mp4 — optional)</Label><Input value={f.video_url} onChange={e => set('video_url', e.target.value)} placeholder="Paste a link, or leave blank for now" data-testid="nm-video" /></div>
          <div><Label className="text-xs">Cover image URL (optional)</Label><Input value={f.cover_url} onChange={e => set('cover_url', e.target.value)} data-testid="nm-cover" /></div>
          <div><Label className="text-xs">Short description</Label><Textarea value={f.description} onChange={e => set('description', e.target.value)} rows={2} data-testid="nm-desc" /></div>
          <div>
            <Label className="text-xs">Transcript / key points (the bot reads this to build the quiz)</Label>
            <Textarea value={f.transcript} onChange={e => set('transcript', e.target.value)} rows={4} placeholder="Paste what the video covers — the AI uses this to write the knowledge check." data-testid="nm-transcript" />
          </div>
          <div>
            <Label className="text-xs">Assign to roles (leave empty = everyone)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {ROLES.map(r => {
                const on = f.assigned_roles.includes(r);
                return <button key={r} onClick={() => set('assigned_roles', on ? f.assigned_roles.filter(x => x !== r) : [...f.assigned_roles, r])}
                  className={`text-xs rounded-full px-3 py-1.5 border capitalize ${on ? 'bg-[#F26522] text-white border-[#F26522]' : 'bg-white text-slate-600 border-slate-200'}`} data-testid={`nm-role-${r}`}>{r}</button>;
              })}
            </div>
          </div>
          <Button onClick={generate} disabled={gen} variant="outline" className="w-full rounded-xl border-[#F26522]/40 text-[#F26522] hover:bg-orange-50" data-testid="nm-generate-quiz">
            {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate quiz with AI</>}
          </Button>
          {quiz.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-2" data-testid="nm-quiz-preview">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Generated quiz ({quiz.length})</p>
              {quiz.map((q, i) => (
                <div key={i} className="text-sm">
                  <p className="font-medium text-slate-800">{i + 1}. {q.q}</p>
                  <ul className="mt-0.5 space-y-0.5">{q.options.map((o, oi) => <li key={oi} className={`text-xs pl-3 ${oi === q.answer_index ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>{oi === q.answer_index ? '✓ ' : '• '}{o}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
          <Button onClick={save} disabled={saving} className="w-full bg-[#3E2418] hover:bg-[#2a180f] text-white rounded-xl h-11 font-semibold" data-testid="nm-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create module'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TeamDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { ops.get('/training/dashboard').then(setD).catch(e => toast.error(e.response?.data?.detail || 'Failed to load')); }, []);
  if (!d) return <div className="py-8 text-slate-500 text-sm" data-testid="team-loading">Loading team progress…</div>;
  return (
    <div className="space-y-5" data-testid="team-dashboard">
      <div className="grid grid-cols-3 gap-3">
        {[['Staff in scope', d.overall.staff], ['Active modules', d.overall.modules], ['Avg completion', `${d.overall.avg_pct}%`]].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#E5E1DA] rounded-2xl p-4">
            <p className="text-2xl font-black text-[#3E2418]" style={{ fontFamily: 'Chivo, sans-serif' }}>{v}</p>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-1">{l}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5">
        <h3 className="font-bold text-[#3E2418] text-sm mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#F26522]" />Completion by module</h3>
        <div className="space-y-2.5">
          {d.modules.map(m => (
            <div key={m.id} data-testid={`team-module-${m.id}`}>
              <div className="flex items-center justify-between text-sm mb-1"><span className="font-medium text-slate-700 truncate pr-2">{m.title}</span><span className="text-slate-500 shrink-0">{m.completed}/{m.assigned} · {m.pct}%</span></div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#F26522] rounded-full" style={{ width: `${m.pct}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E1DA] rounded-2xl p-4 sm:p-5">
        <h3 className="font-bold text-[#3E2418] text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#F26522]" />Staff needing attention</h3>
        <div className="divide-y divide-slate-100">
          {d.staff.slice(0, 20).map(s => (
            <div key={s.user_id} className="flex items-center justify-between py-2.5" data-testid={`team-staff-${s.user_id}`}>
              <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{s.name}</p><p className="text-xs text-slate-400 truncate capitalize">{s.role} · {s.store_name || 'No store'}</p></div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">{s.completed}/{s.assigned}</span>
                <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${s.pct >= 100 ? 'bg-emerald-100 text-emerald-700' : s.pct >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-700'}`}>{s.pct}%</span>
              </div>
            </div>
          ))}
          {d.staff.length === 0 && <p className="text-sm text-slate-400 py-4">No crew members assigned to your stores yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default function Training() {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [me, setMe] = useState(null);
  const [cat, setCat] = useState('All');
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState('learn'); // learn | team

  const load = () => {
    ops.get('/training/modules').then(r => { setModules(r.modules); setCategories(r.categories); }).catch(e => toast.error(e.response?.data?.detail || 'Failed to load'));
    ops.get('/training/me').then(setMe).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cats = ['All', ...Array.from(new Set(modules.map(m => m.category)))];
  const shown = cat === 'All' ? modules : modules.filter(m => m.category === cat);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="training-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#F26522] font-semibold">Training</p>
          <h1 className="text-2xl sm:text-3xl font-black text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>A&W Operations LMS</h1>
        </div>
        {mgmt && (
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-full p-0.5" data-testid="training-tabs">
              <button onClick={() => setTab('learn')} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${tab === 'learn' ? 'bg-white text-[#3E2418] shadow-sm' : 'text-slate-500'}`} data-testid="tab-learn">My learning</button>
              <button onClick={() => setTab('team')} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${tab === 'team' ? 'bg-white text-[#3E2418] shadow-sm' : 'text-slate-500'}`} data-testid="tab-team">Team</button>
            </div>
            <Button onClick={() => setShowNew(true)} size="sm" className="bg-[#F26522] hover:bg-[#d9551a] text-white rounded-full h-9 px-4" data-testid="new-module-btn"><Plus className="w-4 h-4 mr-1" />New module</Button>
          </div>
        )}
      </div>

      {(!mgmt || tab === 'learn') && (
        <>
          {/* Progress banner */}
          {me && (
            <section className="rounded-2xl bg-[#3E2418] p-5 flex items-center gap-5" data-testid="training-progress-banner">
              <Ring pct={me.pct} />
              <div className="flex-1">
                <p className="text-white font-bold text-lg leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{me.completed} of {me.assigned} modules complete</p>
                <p className="text-white/60 text-xs mt-0.5 capitalize">{me.role.replace('_', ' ')} learning path</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 text-xs text-amber-300 font-semibold"><Flame className="w-3.5 h-3.5" />{me.streak}-day streak</span>
                  {me.pct === 100 && me.assigned > 0 && <span className="inline-flex items-center gap-1 text-xs text-emerald-300 font-semibold"><Trophy className="w-3.5 h-3.5" />All done!</span>}
                </div>
              </div>
            </section>
          )}

          {/* Category filter */}
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="category-filter">
            {cats.map(c => (
              <button key={c} onClick={() => setCat(c)} className={`whitespace-nowrap text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${cat === c ? 'bg-[#F26522] text-white border-[#F26522]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid={`cat-${c}`}>{c}</button>
            ))}
          </div>

          {/* Module grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="module-grid">
            {shown.map(m => {
              const st = STATUS[m.progress.status] || STATUS.not_started;
              return (
                <button key={m.id} onClick={() => setActive(m)} className="text-left bg-white border border-[#E5E1DA] rounded-2xl overflow-hidden hover:shadow-md hover:border-[#F26522]/40 transition-all active:scale-[0.99] flex flex-col" data-testid={`module-card-${m.id}`}>
                  <div className="relative aspect-video bg-slate-100">
                    {m.cover_url && <img src={m.cover_url} className="w-full h-full object-cover" alt={m.title} loading="lazy" />}
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center"><div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center"><Play className="w-5 h-5 text-[#F26522] ml-0.5" /></div></div>
                    <span className="absolute bottom-2 right-2 text-[10px] font-semibold bg-black/60 text-white rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{m.duration_min || 3}m</span>
                    {m.progress.status === 'completed' && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-white" /></span>}
                  </div>
                  <div className="p-3.5 flex-1 flex flex-col">
                    <p className="text-[10px] uppercase tracking-wider text-[#F26522] font-semibold">{m.category}</p>
                    <p className="text-sm font-bold text-[#3E2418] leading-snug mt-0.5" style={{ fontFamily: 'Chivo, sans-serif' }}>{m.title}</p>
                    <div className="flex items-center justify-between mt-auto pt-2.5">
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                      <span className="text-[11px] text-slate-400">{m.question_count} Q</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {shown.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No training modules assigned to you yet.</p>}
        </>
      )}

      {mgmt && tab === 'team' && <TeamDashboard />}

      {active && <QuizModal module={active} onClose={() => setActive(null)} onDone={load} />}
      {showNew && <NewModuleModal categories={categories} onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}
